import React, { useContext, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
// 디자인 토큰 (앱 전체 CSS 변수)
import './studio/styles/designTokens.css';
// import './utils/debug'; // 디버깅 유틸리티 추가
import { setupGlobalFetchInterceptor, setupGlobalTokenMonitor } from './studio/utils/auth'; // 전역 fetch 인터셉터
import LoginPage from './studio/pages/LoginPage';
import RegisterPage from './studio/pages/RegisterPage';
import CalendarPage from './studio/pages/CalendarPage'
import AdminPage from './studio/pages/AdminPage'
import ClientPage from './studio/pages/ClientPage'
import StaffArtists from './studio/pages/admin/StaffArtists'
import GuestArtists from './studio/pages/admin/GuestArtists'
import { UserContext } from './studio/contexts/UserContext';
import Users from './studio/pages/admin/Users'
import AdminCalendarPage from './studio/pages/AdminCalendarPage'
import ArtistAvailabilityCalendar from './studio/components/ArtistAvailabilityCalendar';
import GuestAvailabilityCalendar from './studio/components/GuestAvailabilityCalendar';
import FloatingUserMenu from './studio/components/FloatingUserMenu';
import LandingPage from './studio/pages/LandingPage';
import ProtectedRoute from './studio/components/ProtectedRoute';
import EditProfilePage from './studio/pages/EditProfilePage';
// [REMOVED] SNS module deleted
import ArtistReservationManagerPage from './studio/pages/ArtistReservationManagerPage';
// [ARCHIVED] ArtistReservationList moved to Archive folder
import ManualReservationList from './studio/components/ManualReservationList';
import MyPage from './studio/pages/MyPage';
import FlashAdd from './studio/components/FlashAdd';
import GestionMyPage from './studio/pages/GestionMyPage';
// [REMOVED] PersonalProjectListPage - unused legacy page
// [REMOVED] FlashProjectListPage - unused legacy page
// [REMOVED] MessageDMPage - unused legacy page (DM feature archived)
// [ARCHIVED] ClientReservationPage moved to Archive folder
import SettingsPage from './studio/pages/SettingsPage';
import StyleGuidePage from './studio/pages/StyleGuidePage';
import GuidePage from './studio/pages/GuidePage';
import PublicCalendarWrapperPage from './studio/pages/PublicCalendarWrapperPage';
import PoopDodgeGame from './studio/components/PoopDodgeGame';
import GuestHomePage from './studio/pages/GuestHomePage';
import GuestCalendarPage from './studio/pages/GuestCalendarPage';
import GuestReservationListPage from './studio/pages/GuestReservationListPage';
import GuestGuidePage from './studio/pages/GuestGuidePage';
import AdminGuestRegistrationPage from './studio/pages/AdminGuestRegistrationPage';
import GuestConditionsPage from './studio/pages/GuestConditionsPage';
// [REMOVED] SNS pages and components deleted
import SpecialPage from './studio/pages/SpecialPage';
import StudioManagementPage from './studio/pages/StudioManagementPage';
import CommissionManagementPage from './studio/pages/CommissionManagementPage';
import StockManagementPage from './studio/pages/StockManagementPage';
import ExpensesPage from './studio/pages/ExpensesPage';
import FlashBookPage from './studio/pages/FlashBookPage';
import PlancheEditorPage from './studio/pages/PlancheEditorPage';
import NotificationSettingsPage from './studio/pages/NotificationSettingsPage';
import PwaInstallGuidePage from './studio/pages/PwaInstallGuidePage';
import PwaInstallPromotion from './studio/components/PwaInstallPromotion';
import { PERMISSIONS } from './studio/utils/permissionConfig';
import {
  isNotificationEligibleUserLevel,
  reconcileCurrentSubscription
} from './studio/services/notificationService';

function App() {
  const { userInfo, isUserLoading } = useContext(UserContext);
  const location = useLocation();
  const isLanding = location.pathname === '/';
  const isLogin = location.pathname === '/login';

  const isPublicCalendar = location.pathname === '/cal' || location.pathname === '/public-calendar';
  const isExpenses = location.pathname === '/expenses';
  const isArtistPage = location.pathname === '/artist-page' || location.pathname.startsWith('/artist-page/');
  // isSnsPage removed - SNS module deleted
  // isSpecialPage removed - not currently used\n  // const isSpecialPage = location.pathname === '/special';
  const isGuestCalendar = location.pathname.toLowerCase() === '/guestcalendar';
  const is404Page = ![
    '/', '/login', '/client', '/register', '/calendar', '/admin', '/cal', '/GuestCalendar',
    '/public-calendar', '/guest-conditions', '/me', '/artist/reservations', '/developtest', '/manual-reservations', '/artist/manual-reservations',
    '/mypage', '/flash', '/gestion', '/settings', '/guide', '/guest', '/game', '/special',
    '/studio-management', '/commission-management', '/stock-management', '/flashbook', '/expenses', '/notification-settings', '/install-app'
  ].some(path => location.pathname === path || location.pathname.startsWith(path + '/'));

  // 앱 시작 시 전역 fetch 인터셉터 및 토큰 모니터 설정
  useEffect(() => {
    let isMounted = true;
    let tokenMonitorTimeout = null;

    // 전역 unhandledrejection 에러 핸들러 설정
    const handleGlobalRejection = (event) => {
      if (event.reason?.name === 'AbortError') {
        console.log('전역: Fetch 요청이 정상적으로 중단되었습니다.');
        event.preventDefault(); // 에러 이벤트 전파 방지
        return;
      }
      // 다른 에러는 그대로 처리
      console.error('전역: 처리되지 않은 Promise 거부:', event.reason);
    };

    // 전역 에러 이벤트 리스너 등록
    window.addEventListener('unhandledrejection', handleGlobalRejection);

    const initializeApp = async () => {
      try {
        setupGlobalFetchInterceptor();

        // 공개 페이지가 아닌 경우에만 토큰 검사 실행
        if (!isPublicCalendar && isMounted) {
          const { checkTokenAndLogout } = await import('./studio/utils/auth');
          if (checkTokenAndLogout()) {
            return; // 토큰이 만료되었으면 더 이상 진행하지 않음
          }
        }

        // 전역 토큰 모니터는 약간의 지연 후 설정 (무한 루프 방지)
        if (isMounted) {
          tokenMonitorTimeout = setTimeout(() => {
            // 공개 페이지가 아닌 경우에만 토큰 모니터 설정
            if (!isPublicCalendar && isMounted) {
              setupGlobalTokenMonitor();
            }
          }, 1000); // 1초로 단축
        }
      } catch (error) {
        console.error('앱 초기화 실패:', error);
      }
    };

    initializeApp();

    return () => {
      isMounted = false;
      if (tokenMonitorTimeout) {
        clearTimeout(tokenMonitorTimeout);
      }
    };
  }, [isPublicCalendar]);

  useEffect(() => {
    if (!userInfo?.user_id || !isNotificationEligibleUserLevel(userInfo.level)) {
      return undefined;
    }

    const reconcile = () => {
      reconcileCurrentSubscription(userInfo.user_id, userInfo.level).catch(() => {
        // 권한 요청은 하지 않고 다음 앱 실행 또는 설정 페이지에서 다시 대조한다.
      });
    };

    reconcile();
    window.addEventListener('push-subscription-change', reconcile);
    return () => window.removeEventListener('push-subscription-change', reconcile);
  }, [userInfo?.level, userInfo?.user_id]);



  if (isUserLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        color: '#333'
      }}>
        로딩 중...
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      height: '100%', /* html/body/#root의 100vh -> 100dvh 폴백 체인을 그대로 사용 */
      minHeight: 0,
      position: 'relative',
      backgroundColor: isLogin ? '#000' : '#fff', /* 로그인 페이지는 검은색 배경 */
      color: isLogin ? '#00ff00' : '#000', /* 로그인 페이지는 초록색 텍스트 */
      boxSizing: 'border-box',
      overflow: (isPublicCalendar || isLanding || isExpenses) ? 'auto' : 'hidden'
    }}>
      {userInfo && !isLanding && !isArtistPage && !isGuestCalendar && !is404Page && <FloatingUserMenu />}
      {userInfo && <PwaInstallPromotion />}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/client" element={<ProtectedRoute allowedLevels={['admin', 'staff_artist', 'guest_artist', 'client']}><ClientPage /></ProtectedRoute>} />
        <Route path="/register" element={<ProtectedRoute allowedLevels={['admin']} allowedPermissions={[PERMISSIONS.ADMIN_ACCESS]}><RegisterPage /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute allowedLevels={['guest_artist', 'admin', 'staff_artist']}><CalendarPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute allowedLevels={['admin']} allowedPermissions={[PERMISSIONS.ADMIN_ACCESS]}><AdminPage /></ProtectedRoute>} />
        <Route path="/admin/staff-artists" element={<ProtectedRoute allowedLevels={['admin']} allowedPermissions={[PERMISSIONS.ADMIN_ACCESS]}><StaffArtists /></ProtectedRoute>} />
        <Route path="/admin/guest-artists" element={<ProtectedRoute allowedLevels={['admin']} allowedPermissions={[PERMISSIONS.ADMIN_ACCESS]}><GuestArtists /></ProtectedRoute>} />
        <Route path="/admin/guest-registration" element={<ProtectedRoute allowedLevels={['admin']} allowedPermissions={[PERMISSIONS.ADMIN_ACCESS]}><AdminGuestRegistrationPage /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute allowedLevels={['admin']} allowedPermissions={[PERMISSIONS.ADMIN_ACCESS]}><Users /></ProtectedRoute>} />
        <Route path="/admin/AdminCalendarPage" element={<ProtectedRoute allowedLevels={['admin']} allowedPermissions={[PERMISSIONS.ADMIN_ACCESS]}><AdminCalendarPage /></ProtectedRoute>} />
        <Route path="/admin/style-guide" element={<ProtectedRoute allowedLevels={['admin']} allowedPermissions={[PERMISSIONS.ADMIN_ACCESS]}><StyleGuidePage /></ProtectedRoute>} />
        <Route path="/cal" element={<ArtistAvailabilityCalendar />} />
        <Route path="/GuestCalendar" element={<GuestAvailabilityCalendar />} />
        <Route path="/public-calendar" element={<PublicCalendarWrapperPage />} />
        <Route path="/guest-conditions" element={<GuestConditionsPage />} />
        <Route path="/guest-conditions/:lang" element={<GuestConditionsPage />} />
        <Route path="/me" element={<ProtectedRoute allowedLevels={['admin', 'staff_artist', 'guest_artist', 'client']}><EditProfilePage /></ProtectedRoute>} />
        <Route
          path="/artist/reservations"
          element={
            <ProtectedRoute allowedLevels={['admin', 'staff_artist']}>
              <ArtistReservationManagerPage />
            </ProtectedRoute>
          }
        />
        {/* [ARCHIVED] /artist/reservations/list route removed - use /manual-reservations instead */}
        <Route
          path="/developtest"
          element={
            <ProtectedRoute allowedLevels={['admin', 'staff_artist']}>
              <ManualReservationList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manual-reservations"
          element={
            <ProtectedRoute allowedLevels={['admin', 'staff_artist']}>
              <ManualReservationList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/artist/manual-reservations"
          element={
            <ProtectedRoute allowedLevels={['admin', 'staff_artist']}>
              <ManualReservationList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mypage"
          element={
            <ProtectedRoute allowedLevels={['admin', 'staff_artist', 'guest_artist', 'client']}>
              <MyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/flash/add"
          element={
            <ProtectedRoute allowedLevels={['admin', 'staff_artist']}>
              <FlashAdd />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gestion"
          element={
            <ProtectedRoute allowedLevels={['admin', 'staff_artist', 'guest_artist']}>
              <GestionMyPage />
            </ProtectedRoute>
          }
        />
        {/* [REMOVED] /personal-projects route - unused legacy page */}
        {/* [REMOVED] public artist page routes - unused customer-facing flow */}
        {/* [REMOVED] /artist/:username/personal-project-request route - unused customer-facing flow */}
        {/* [REMOVED] /flash-projects route - unused legacy page */}
        {/* [REMOVED] /messages route - unused inbox page */}
        {/* [REMOVED] /dm route - unused legacy page (DM feature archived) */}
        {/* [ARCHIVED] /client/reservations route removed */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute allowedLevels={['admin', 'staff_artist', 'guest_artist', 'client']}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notification-settings"
          element={
            <ProtectedRoute allowedLevels={['admin', 'staff_artist', 'guest_artist', 'client']}>
              <NotificationSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/install-app"
          element={
            <ProtectedRoute allowedLevels={['admin', 'staff_artist', 'guest_artist', 'client']}>
              <PwaInstallGuidePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guide"
          element={
            <ProtectedRoute allowedLevels={['admin', 'staff_artist', 'guest_artist', 'client']}>
              <GuidePage />
            </ProtectedRoute>
          }
        />

        {/* 게스트 아티스트 전용 라우트 */}
        <Route path="/guest" element={<ProtectedRoute allowedLevels={['guest_artist']}><GuestHomePage /></ProtectedRoute>} />
        <Route path="/guest/calendar" element={<ProtectedRoute allowedLevels={['guest_artist']}><GuestCalendarPage /></ProtectedRoute>} />
        <Route path="/guest/reservations" element={<ProtectedRoute allowedLevels={['guest_artist']}><GuestReservationListPage /></ProtectedRoute>} />
        <Route path="/guest/guide" element={<ProtectedRoute allowedLevels={['guest_artist']}><GuestGuidePage /></ProtectedRoute>} />

        <Route path="/game" element={<PoopDodgeGame />} />

        {/* [REMOVED] SNS routes deleted */}

        {/* 특별 페이지 라우트 */}
        <Route path="/special" element={<ProtectedRoute allowedPermissions={[PERMISSIONS.CHANNEL_ADMIN]}><SpecialPage /></ProtectedRoute>} />
        <Route path="/studio-management" element={<ProtectedRoute allowedPermissions={[PERMISSIONS.COMMISSION_ACCESS]}><StudioManagementPage /></ProtectedRoute>} />
        <Route path="/commission-management" element={<ProtectedRoute allowedPermissions={[PERMISSIONS.COMMISSION_ACCESS]}><CommissionManagementPage /></ProtectedRoute>} />
        <Route path="/stock-management" element={<ProtectedRoute allowedPermissions={[PERMISSIONS.COMMISSION_ACCESS]}><StockManagementPage /></ProtectedRoute>} />
        <Route path="/expenses" element={<ProtectedRoute allowedPermissions={[PERMISSIONS.EXPENSES_ACCESS]}><ExpensesPage /></ProtectedRoute>} />

        {/* 플래시북 라우트 */}
        <Route path="/flashbook" element={<ProtectedRoute allowedLevels={['staff_artist']}><FlashBookPage /></ProtectedRoute>} />
        <Route path="/flashbook/planche/:id" element={<ProtectedRoute allowedLevels={['staff_artist']}><PlancheEditorPage /></ProtectedRoute>} />

        {/* 404 Not Found */}
        <Route path="*" element={
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: '1rem',
            color: '#666'
          }}>
            <h1 style={{ fontSize: '4rem', margin: 0, color: '#333' }}>404</h1>
            <p style={{ fontSize: '1.2rem', margin: 0 }}>Page Not Found</p>
            <a href="/" style={{ color: '#007bff', textDecoration: 'none' }}>Go to Home</a>
          </div>
        } />
      </Routes>
    </div>
  );
}

export default App;
